// Запросы входа и сессии (задача D4): кто вошёл (`meQuery`), вход (`useLogin`) и выход (`useLogout`).
import {
  LogoutResponseSchema,
  MeSchema,
  type LoginBody,
  type Me,
  type Role,
} from '@contract/index.js';
import { queryOptions, useMutation, useQuery, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { closeSessionExpired } from '@/auth/session';
import { apiGet, apiSend, LOGIN_PATH, LOGOUT_PATH } from '../client';
import { ApiError } from '../errors';
import { qk } from '../keys';

export const ME_PATH = '/api/v1/auth/me';

/** Кто вошёл и до какого момента действует сессия. Корневой загрузчик пульта кладёт ответ в кэш до первого экрана. */
export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: ({ signal }) => apiGet(ME_PATH, MeSchema, { signal }),
});

/** Вошедший пользователь (внутри пульта он уже в кэше: его загрузил корневой загрузчик). */
export function useMe(): Me | undefined {
  return useQuery(meQuery).data;
}

/** Роль вошедшего; `undefined` — пока неизвестна (экран вне пульта). */
export function useRole(): Role | undefined {
  return useMe()?.user.role;
}

/** Все запросы, кроме «кто вошёл»: их перезапрашивают после входа. */
function exceptMe(query: Query): boolean {
  return query.queryKey[0] !== qk.me[0];
}

/**
 * После входа: новый `me` в кэш, окно «Сессия истекла» закрыто, данные экрана перезапрошены. Вошёл тот же человек —
 * прежние данные видны, пока идёт перезапрос; другой — данные прежнего сбрасываются сразу (у админа в них полные
 * телефоны, а войти мог и «Просмотр»).
 */
function applyLogin(queryClient: QueryClient, me: Me): void {
  const before = queryClient.getQueryData(meQuery.queryKey);
  queryClient.setQueryData(meQuery.queryKey, me);
  closeSessionExpired();
  if (before !== undefined && before.user.id !== me.user.id) {
    void queryClient.resetQueries({ predicate: exceptMe });
  } else {
    void queryClient.invalidateQueries({ predicate: exceptMe });
  }
}

/** Вход почтой и паролем Омнидеска. Ошибки — `ApiError` с текстом сервера или `ServiceUnavailableError`. */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginBody) => apiSend('POST', LOGIN_PATH, body, MeSchema),
    onSuccess: (me) => {
      applyLogin(queryClient, me);
    },
  });
}

/**
 * Выход: сервер закрывает сессию и стирает cookie, пульт уходит на `/login` и забывает данные прежнего пользователя.
 * 401 — сессии и так нет, это тоже выход. Сервис не ответил — выход не подтверждён: остаёмся на месте, экран покажет
 * ошибку мутации.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      try {
        await apiSend('POST', LOGOUT_PATH, undefined, LogoutResponseSchema);
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 401)) throw error;
      }
    },
    onSuccess: async () => {
      closeSessionExpired();
      // Сначала уходим с экранов пульта, потом чистим кэш: иначе открытый экран успел бы перезапросить свои данные.
      await navigate('/login', { replace: true });
      queryClient.clear();
    },
  });
}
